import { Component, ErrorHandler, Inject, Injector, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { FormValidationService } from 'app/_jaettu-angular/service/form-validation.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { TiedostojenLataamisService } from 'app/_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { Asiakas, Tilikausi } from 'app/_jaettu-lemonator/model/asiakas'
import { ArkistoFile, ArkistoFileTiedostoTyyppi, ArkistoFileType, ArkistoService } from 'app/_jaettu-lemonator/service/arkisto.service'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { EnvironmentType } from 'app/app.environment'
import { environment } from 'environments/environment'
import { BehaviorSubject, Subject, firstValueFrom, takeUntil } from 'rxjs'
import { FileSystemFileEntry } from '../../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../../_jaettu-angular/file-drop/file-drop-entry'
import { ArkistoFileTiedostoTyyppiNimet } from './kirjanpito-hallinto.component'

export interface ArkistoHallintoUploadDialogData {
  asiakas: Asiakas
  tilikaudet: Tilikausi[]
  valittuTilikausiAvain: string | null
  droppedFile?: FileDropEntry
}

interface ArkistoonLatausLomake {
  lisatiedot: string
  tilikausiAvaimet: string[]
  tiedostoTyyppi: ArkistoFileTiedostoTyyppi | null
}

@Component({
  templateUrl: './arkisto-hallinto-upload.dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ArkistoHallintoUploadDialog implements OnInit {
  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake: FieldTree<ArkistoonLatausLomake>
  tilikaudet: Tilikausi[]
  latausVirheObservable = new BehaviorSubject<string>(null)
  private _fileLoadingSubject = new BehaviorSubject<boolean>(false)
  fileLoadingObservable = this._fileLoadingSubject.asObservable().pipe(takeUntil(this._ngUnsubscribe))
  private _saveStarted = false

  readonly uploadedFile = signal<{ avain: string; fileName: string }>(undefined)

  readonly kayttajanTiedostotyypit: { arvo: ArkistoFileTiedostoTyyppi; nimi: string }[] = [
    ArkistoFileTiedostoTyyppi.VEROTUS,
    ArkistoFileTiedostoTyyppi.YHTIOPAPERIT,
    ArkistoFileTiedostoTyyppi.LAINAPAPERIT,
    ArkistoFileTiedostoTyyppi.VUOKRASOPIMUKSET,
    ArkistoFileTiedostoTyyppi.KIRJEENVAIHTO,
    ArkistoFileTiedostoTyyppi.TILINTARKASTUS,
    ArkistoFileTiedostoTyyppi.MUUTA,
  ].map(arvo => ({ arvo, nimi: ArkistoFileTiedostoTyyppiNimet[arvo] }))

  constructor(
    @Inject(MAT_DIALOG_DATA) private _data: ArkistoHallintoUploadDialogData,
    private _dialogRef: MatDialogRef<ArkistoHallintoUploadDialog>,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _ladataanService: LadataanService,
    private _timestampService: TimestampService,
    private _lemonatorFirebase: FirebaseLemonator,
    private _arkistoService: ArkistoService,
    private _errorHandler: ErrorHandler,
    private _formValidationService: FormValidationService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _dateService: DateService,
    private _asiakasJaettuService: AsiakasJaettuService,
  ) {
    _dialogRef.disableClose = true
  }

  ngOnInit() {
    this.tilikaudet = [...(this._data.tilikaudet ?? [])].sort((a, b) => this._dateService.localDateToNumber(b.alkaa) - this._dateService.localDateToNumber(a.alkaa))

    const alkuarvot: string[] = []
    const valittuSuodattimesta = this._data.valittuTilikausiAvain ? this.tilikaudet.find(tk => tk.avain === this._data.valittuTilikausiAvain) : null

    // Jos ollaan rajattu tilikauteen, käytetään sitä - muuten kuluva tilikausi tai jos ei ole, viimeisin tilikausi
    if (valittuSuodattimesta) {
      alkuarvot.push(valittuSuodattimesta.avain)
    } else if (this.tilikaudet.length > 0) {
      const kuluva = this._asiakasJaettuService.annaTilikausiPaivalle({ tilikaudet: this.tilikaudet }, this._dateService.currentLocalDate())
      alkuarvot.push((kuluva ?? this.tilikaudet[0]).avain)
    }

    this.lomake = form(signal<ArkistoonLatausLomake>({ lisatiedot: '', tilikausiAvaimet: alkuarvot, tiedostoTyyppi: null }), { injector: this._injector })

    if (this._data.droppedFile) {
      this._fileLoadingSubject.next(true)
      this._ladataanService.aloitaLataaminen()
      this._uploadFile([this._data.droppedFile])
        .then(() => {
          this._fileLoadingSubject.next(false)
          this._ladataanService.lopetaLataaminen()
        })
        .catch(err => {
          this._ladataanService.lopetaLataaminen()
          this._fileLoadingSubject.next(false)
          this.latausVirheObservable.next('Lataamisen aikana tapahtui virhe: ' + err)
        })
    }
  }

  async upload(event: Event) {
    this.latausVirheObservable.next(null)
    this._fileLoadingSubject.next(true)
    this._ladataanService.aloitaLataaminen()

    const inputTypeFile = event?.target as HTMLInputElement
    if (inputTypeFile?.files) {
      const list: FileList = inputTypeFile.files
      const tiedostot: FileDropEntry[] = this._tiedostojenLataamisService.fileListToNgxFileDropEntries(list)
      try {
        await this._uploadFile(tiedostot)
        this._fileLoadingSubject.next(false)
        this._ladataanService.lopetaLataaminen()
      } catch (err) {
        this._ladataanService.lopetaLataaminen()
        this._fileLoadingSubject.next(false)
        this.latausVirheObservable.next('Lataamisen aikana tapahtui virhe: ' + err)
      }
    }
  }

  private async _uploadFile(tiedostot: FileDropEntry[]) {
    this._ladataanService.aloitaLataaminen()

    for (const tiedosto of tiedostot) {
      const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)
      const avain = this._lemonatorFirebase.firestoreCreateId()

      this.uploadedFile.set({ avain, fileName: file.name })

      const bucket = environment.environment === EnvironmentType.PRODUCTION ? 'arkisto-public' : 'arkisto-public-test'
      const storageUri = this._arkistoService.getArkistoPublicFileStorageUri(this._data.asiakas.avain, { avain })

      const runningUpload = this._tiedostojenLataamisService.upload(this._lemonatorFirebase, storageUri, file, bucket)
      await firstValueFrom(runningUpload.doneObservable)
    }
  }

  async save() {
    if (!this.lomake().valid()) {
      this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    if (this._saveStarted) {
      return
    }
    this._saveStarted = true

    this._ladataanService.aloitaLataaminen()

    try {
      const kirjanpitaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      const asiakasAvain = this._data.asiakas.avain
      const uploadedFile = this.uploadedFile()
      const metadataAvain = uploadedFile.avain

      const metadata: ArkistoFile = {
        avain: metadataAvain,
        fileName: uploadedFile.fileName,
        asiakasAvain,
        // Koskematon lisätietokenttä tallentui reaktiivisessa lomakkeessa nullina
        lisatiedot: this.lomake.lisatiedot().dirty() ? this.lomake.lisatiedot().value() : null,
        poistettu: false,
        paivitetty: this._timestampService.now(),
        luotu: this._timestampService.now(),
        paivittaja: kirjanpitaja.uid,
        luoja: kirjanpitaja.uid,
        type: ArkistoFileType.VISIBLE_FOR_CUSTOMER,
        tilikaudet: this.lomake.tilikausiAvaimet().value()?.length > 0 ? this.lomake.tilikausiAvaimet().value() : null,
        tiedostoTyyppi: this.lomake.tiedostoTyyppi().value() ?? null,
      }

      const uri = this._arkistoService.getArkistoMetadataUri(asiakasAvain, metadataAvain)
      await this._lemonatorFirebase.firestoreSetData(uri, metadata)

      this._dialogRef.close()
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
    }
  }
}
